import http from '@/lib/http';
import {
  cmsResourceTagBulkActionsPath,
  cmsResourceTagPath,
  cmsResourceTagsPath,
  mergeCmsResourceTagBulkActionsPath,
} from '@/lib/routes';
import type {
  ResourceTagCreateResponse,
  ResourceTagDeleteResponse,
  ResourceTagMergeResponse,
  ResourceTagResponse,
  ResourceTagType,
  ResourceTagUpdateResponse,
  TagOption,
} from './types';

const inFlightTagRequests = new Map<ResourceTagType, Promise<TagOption[]>>();

export async function fetchTags(tagType: ResourceTagType) {
  const inFlightRequest = inFlightTagRequests.get(tagType);
  if (inFlightRequest) return inFlightRequest;

  const request = http
    .get<ResourceTagResponse>(`${cmsResourceTagsPath()}?tag_type=${tagType}`)
    .then((response) => response.data.tags || [])
    .finally(() => {
      // Only retire our own entry; a reset may already have replaced it with a newer request.
      if (inFlightTagRequests.get(tagType) === request) inFlightTagRequests.delete(tagType);
    });

  inFlightTagRequests.set(tagType, request);

  return request;
}

export function clearInFlightTagRequests() {
  inFlightTagRequests.clear();
}

export async function createTag(type: ResourceTagType, name: string) {
  const response = await http.post<ResourceTagCreateResponse>(cmsResourceTagsPath(), {
    tagType: type,
    name: name.trim(),
  });

  return response.data.tag;
}

export async function renameTag(type: ResourceTagType, id: string, name: string) {
  const response = await http.patch<ResourceTagUpdateResponse>(cmsResourceTagPath(id), {
    tagType: type,
    name: name.trim(),
  });

  return response.data.tag;
}

export async function deleteTags(type: ResourceTagType, ids: string[] | string) {
  const normalizedIds = Array.isArray(ids) ? ids : [ids];
  const response = await http.delete<ResourceTagDeleteResponse>(cmsResourceTagBulkActionsPath(), {
    data: { tagType: type, ids: normalizedIds },
  });

  return response.data;
}

export async function mergeTags(
  type: ResourceTagType,
  sourceIds: string[] | string,
  targetId: string,
) {
  const normalizedSourceIds = Array.isArray(sourceIds) ? sourceIds : [sourceIds];
  const response = await http.post<ResourceTagMergeResponse>(mergeCmsResourceTagBulkActionsPath(), {
    tagType: type,
    ids: normalizedSourceIds,
    targetId,
  });

  return response.data.tag;
}
